import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import useCurrentUser from '../hooks/useCurrentUser';
import {
  getDeliveryGuides,
  getSalesMissingDeliveryGuides,
  createBulkDeliveryGuides,
  createTransferDeliveryGuide,
  createOutboundTransferDeliveryGuide,
  getTransferBranches,
  getTransferStockListsByContext,
  getTransferStockListItemsByContext,
  getBranches,
  getClients,
  getIncomingPendingStockRequests,
} from '../api';
import { getSaleStatusMeta } from '../utils/saleStatus';
import { hasPermission } from '../utils/permissions';
import DateRangePresetPicker from '../components/DateRangePresetPicker';
import {
  DELIVERY_GUIDE_STATUS_OPTIONS,
  getDeliveryGuideStatusLabel,
  getDeliveryGuideStatusTone,
  normalizeGuideStatus,
} from '../utils/deliveryGuideStatus';
import { normalizePaginatedResponse } from '../utils/pagination';
import InlineLoading from '../components/InlineLoading';
import SearchMultiSelect from '../components/SearchMultiSelect';

const GUIDE_TYPE_LABELS = {
  sale: 'Venta',
  transfer: 'Traslado',
};

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const sanitizeNumericInput = (value) => value.replace(/[^\d]/g, '');
const formatCurrency = (value) => `$${Number(value || 0).toLocaleString('de-DE')}`;
const normalizeLabel = (value) => String(value || '').trim();
const stripVarietyPrefix = (productName, varietyName) => {
  const base = normalizeLabel(productName).toLowerCase();
  const label = normalizeLabel(varietyName);
  const lowerLabel = label.toLowerCase();
  if (!base || !label) return label;
  if (lowerLabel.startsWith(base)) {
    const stripped = label.slice(base.length).replace(/^[-:\s]+/, '').trim();
    return stripped || label;
  }
  return label;
};
const getRequestItemDisplayName = (item) => {
  const productName = normalizeLabel(item?.Variety?.Product?.productName || item?.productName);
  const varietyName = normalizeLabel(item?.varietyName || item?.Variety?.varietyName);
  if (productName && varietyName) {
    return `${productName} - ${stripVarietyPrefix(productName, varietyName)}`;
  }
  return productName || varietyName || `Variedad #${item?.varietyId ?? '-'}`;
};

const DeliveryGuidesListPage = () => {
  const { branchId: routeBranchId } = useParams();
  const branchId = routeBranchId || localStorage.getItem('selectedBranchId');
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState('guides');
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilters, setStatusFilters] = useState([]);
  const [guidePurchaseSelections, setGuidePurchaseSelections] = useState([]);
  const [guideClientSelections, setGuideClientSelections] = useState([]);
  const [typeFilter, setTypeFilter] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const [guidesPage, setGuidesPage] = useState(1);
  const [guidesTotalPages, setGuidesTotalPages] = useState(1);
  const [guidesTotal, setGuidesTotal] = useState(0);

  const [sales, setSales] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [salesError, setSalesError] = useState('');
  const [salesStartDate, setSalesStartDate] = useState('');
  const [salesEndDate, setSalesEndDate] = useState('');
  const [salesSearch, setSalesSearch] = useState('');
  const [salesClientSelections, setSalesClientSelections] = useState([]);
  const [salesSelections, setSalesSelections] = useState([]);
  const [salesPage, setSalesPage] = useState(1);
  const [salesTotalPages, setSalesTotalPages] = useState(1);
  const [salesTotal, setSalesTotal] = useState(0);
  const [selectedSales, setSelectedSales] = useState([]);
  const [bulkSubmitting, setBulkSubmitting] = useState(false);
  const [bulkError, setBulkError] = useState('');
  const [bulkSuccess, setBulkSuccess] = useState('');
  const [bulkPayload, setBulkPayload] = useState({
    driverName: '',
    vehiclePlate: '',
    deliveryDate: '',
    notes: '',
  });
  const [salesRefreshKey, setSalesRefreshKey] = useState(0);

  const [branches, setBranches] = useState([]);
  const [isMainTransferBranch, setIsMainTransferBranch] = useState(false);
  const [transferMode, setTransferMode] = useState('inbound');
  const [transferBranchOptions, setTransferBranchOptions] = useState([]);
  const [transferSourceBranchId, setTransferSourceBranchId] = useState('');
  const [transferDestinationBranchId, setTransferDestinationBranchId] = useState('');
  const [transferStockLists, setTransferStockLists] = useState([]);
  const [transferStockListSelections, setTransferStockListSelections] = useState([]);
  const [transferStockItems, setTransferStockItems] = useState([]);
  const [transferQuantities, setTransferQuantities] = useState({});
  const [transferExpenses, setTransferExpenses] = useState([]);
  const [transferRequestOptions, setTransferRequestOptions] = useState([]);
  const [transferRequestSelections, setTransferRequestSelections] = useState([]);
  const [transferPendingRequests, setTransferPendingRequests] = useState([]);
  const [transferPrefillNotice, setTransferPrefillNotice] = useState('');
  const [transferPayload, setTransferPayload] = useState({
    stockListName: '',
    guideNumber: '',
    driverName: '',
    vehiclePlate: '',
    deliveryDate: '',
    notes: '',
  });
  const [transferLoading, setTransferLoading] = useState(false);
  const [transferSubmitting, setTransferSubmitting] = useState(false);
  const [transferError, setTransferError] = useState('');
  const [transferSuccess, setTransferSuccess] = useState('');
  const [transferRefreshKey, setTransferRefreshKey] = useState(0);
  const [pendingPrefillRequestListId, setPendingPrefillRequestListId] = useState(null);
  const [transferPrefillApplied, setTransferPrefillApplied] = useState(false);
  const [autoPrefillContextKey, setAutoPrefillContextKey] = useState('');
  const { user } = useCurrentUser();
  const canReadGuides = hasPermission(user, 'delivery_guide.read');
  const canCreateGuide = hasPermission(user, 'delivery_guide.create');
  const canBulkCreate = hasPermission(user, 'delivery_guide.bulk');
  const canTransferGuide = hasPermission(user, 'delivery_guide.transfer');
  const canReadSales = hasPermission(user, 'sales.read');
  const canReadStock = hasPermission(user, 'stock.read');
  const effectiveTransferSourceBranchId = transferMode === 'outbound'
    ? branchId
    : transferSourceBranchId;
  const selectedTransferStockListIds = useMemo(
    () => (transferStockListSelections || [])
      .map((entry) => Number(entry.id))
      .filter((entry) => Number.isInteger(entry)),
    [transferStockListSelections]
  );

  useEffect(() => {
    const fetchGuides = async () => {
      if (activeTab !== 'guides') return;
      if (!canReadGuides) return;
      if (!branchId) return;
      setLoading(true);
      setError('');
      try {
        const params = {
          page: guidesPage,
          pageSize: 12,
        };
        if (startDate) params.startDate = startDate;
        if (endDate) params.endDate = endDate;
        if (statusFilters.length) params.status = statusFilters.join(',');
        if (typeFilter) params.guideType = typeFilter;
        if (guidePurchaseSelections.length) {
          params.purchaseIds = guidePurchaseSelections.map((item) => item.id).join(',');
        }
        if (guideClientSelections.length) {
          params.clientIds = guideClientSelections.map((item) => item.id).join(',');
        }
        const data = await getDeliveryGuides(branchId, params);
        const normalized = normalizePaginatedResponse(data);
        setGuides(normalized.data || []);
        setGuidesTotal(normalized.total || 0);
        setGuidesTotalPages(
          normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / 12))
        );
      } catch (err) {
        setError(err.response?.data?.message || 'No se pudieron cargar las guías.');
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, [
    activeTab,
    branchId,
    startDate,
    endDate,
    statusFilters,
    guidePurchaseSelections,
    guideClientSelections,
    typeFilter,
    refreshKey,
    canReadGuides,
    guidesPage,
  ]);

  useEffect(() => {
    if (activeTab !== 'guides') return;
    setGuidesPage(1);
  }, [activeTab, startDate, endDate, statusFilters, guidePurchaseSelections, guideClientSelections, typeFilter]);

  useEffect(() => {
    if (guidesPage > guidesTotalPages) {
      setGuidesPage(guidesTotalPages);
    }
  }, [guidesPage, guidesTotalPages]);

  useEffect(() => {
    const canAccessSales = canReadSales && (canCreateGuide || canBulkCreate);
    const canAccessTransfer = canTransferGuide && canReadStock;
    if (activeTab === 'sales' && !canAccessSales) {
      setActiveTab('guides');
      return;
    }
    if (activeTab === 'transfer' && !canAccessTransfer) {
      setActiveTab('guides');
      return;
    }
    if (activeTab === 'guides' && !canReadGuides) {
      if (canAccessSales) {
        setActiveTab('sales');
      } else if (canAccessTransfer) {
        setActiveTab('transfer');
      }
    }
  }, [
    activeTab,
    canReadGuides,
    canReadSales,
    canCreateGuide,
    canBulkCreate,
    canTransferGuide,
    canReadStock,
  ]);

  useEffect(() => {
    const fetchBranches = async () => {
      if (!branchId) return;
      try {
        const data = await getBranches({ pageSize: 0 });
        const normalized = normalizePaginatedResponse(data);
        setBranches(normalized.data || []);
      } catch (err) {
        console.error('Error fetching branches', err);
      }
    };
    fetchBranches();
  }, [branchId]);

  useEffect(() => {
    const fetchSales = async () => {
      if (activeTab !== 'sales') return;
      if (!canReadSales) return;
      if (!branchId) return;
      setSalesLoading(true);
      setSalesError('');
      try {
        const params = {
          page: salesPage,
          pageSize: 12,
        };
        if (salesStartDate) params.startDate = salesStartDate;
        if (salesEndDate) params.endDate = salesEndDate;
        if (salesSearch) params.search = salesSearch;
        if (salesClientSelections.length) {
          params.clientIds = salesClientSelections.map((item) => item.id).join(',');
        }
        if (salesSelections.length) {
          params.purchaseIds = salesSelections.map((item) => item.id).join(',');
        }
        const data = await getSalesMissingDeliveryGuides(branchId, params);
        const normalized = normalizePaginatedResponse(data);
        setSales(normalized.data || []);
        setSalesTotal(normalized.total || 0);
        setSalesTotalPages(
          normalized.totalPages || Math.max(1, Math.ceil((normalized.total || 0) / 12))
        );
      } catch (err) {
        setSalesError(err.response?.data?.message || 'No se pudieron cargar las ventas.');
      } finally {
        setSalesLoading(false);
      }
    };

    fetchSales();
  }, [
    activeTab,
    branchId,
    salesStartDate,
    salesEndDate,
    salesSearch,
    salesSelections,
    salesClientSelections,
    salesRefreshKey,
    canReadSales,
    salesPage,
  ]);

  useEffect(() => {
    if (activeTab !== 'sales') return;
    setSalesPage(1);
  }, [activeTab, salesStartDate, salesEndDate, salesSearch, salesSelections, salesClientSelections]);

  useEffect(() => {
    if (salesPage > salesTotalPages) {
      setSalesPage(salesTotalPages);
    }
  }, [salesPage, salesTotalPages]);

  useEffect(() => {
    const detectTransferMode = async () => {
      if (activeTab !== 'transfer') return;
      if (!canReadStock || !canTransferGuide) return;
      if (!branchId) return;
      try {
        await getIncomingPendingStockRequests(branchId, { pageSize: 1 });
        setIsMainTransferBranch(true);
        setTransferMode('outbound');
      } catch (_err) {
        setIsMainTransferBranch(false);
        setTransferMode('inbound');
      }
    };
    detectTransferMode();
  }, [activeTab, canReadStock, canTransferGuide, branchId]);

  useEffect(() => {
    const prefill = location.state?.prefillTransfer;
    if (!prefill || transferPrefillApplied) return;
    if (!canReadStock || !canTransferGuide) return;
    setActiveTab('transfer');
    setTransferMode('outbound');
    if (prefill.destinationBranchId) {
      setTransferDestinationBranchId(String(prefill.destinationBranchId));
    }
    if (prefill.requestListId) {
      setPendingPrefillRequestListId(Number(prefill.requestListId));
    }
    setTransferPrefillApplied(true);
  }, [
    location.state,
    transferPrefillApplied,
    canReadStock,
    canTransferGuide,
  ]);

  useEffect(() => {
    const fetchTransferBranches = async () => {
      if (activeTab !== 'transfer') return;
      if (!canReadStock || !canTransferGuide) return;
      if (!branchId) return;
      setTransferLoading(true);
      setTransferError('');
      try {
        const response = await getTransferBranches(branchId, { mode: transferMode });
        setTransferBranchOptions(Array.isArray(response?.data) ? response.data : []);
      } catch (err) {
        setTransferBranchOptions([]);
        setTransferError(err.response?.data?.message || 'No se pudieron cargar las sucursales.');
      } finally {
        setTransferLoading(false);
      }
    };
    fetchTransferBranches();
  }, [activeTab, canReadStock, canTransferGuide, branchId, transferMode, transferRefreshKey]);

  useEffect(() => {
    if (activeTab !== 'transfer') return;
    setTransferStockListSelections([]);
    setTransferStockItems([]);
    setTransferQuantities({});
    setTransferRequestSelections([]);
    setTransferRequestOptions([]);
    setTransferPendingRequests([]);
    setTransferPrefillNotice('');
    setAutoPrefillContextKey('');
  }, [activeTab, transferMode, transferSourceBranchId]);

  useEffect(() => {
    const fetchStockLists = async () => {
      if (activeTab !== 'transfer') return;
      if (!canReadStock || !canTransferGuide) return;
      if (!effectiveTransferSourceBranchId) {
        setTransferStockLists([]);
        return;
      }
      setTransferLoading(true);
      setTransferError('');
      try {
        const lists = await getTransferStockListsByContext(branchId, {
          mode: transferMode,
          sourceBranchId: effectiveTransferSourceBranchId,
        });
        setTransferStockLists(Array.isArray(lists?.data) ? lists.data : []);
      } catch (err) {
        setTransferError(err.response?.data?.message || 'No se pudieron cargar las listas.');
      } finally {
        setTransferLoading(false);
      }
    };
    fetchStockLists();
  }, [
    activeTab,
    transferMode,
    effectiveTransferSourceBranchId,
    transferRefreshKey,
    canReadStock,
    canTransferGuide,
    branchId,
  ]);

  useEffect(() => {
    const fetchStockListDetail = async () => {
      if (activeTab !== 'transfer') return;
      if (!canReadStock || !canTransferGuide) return;
      if (!effectiveTransferSourceBranchId || selectedTransferStockListIds.length === 0) {
        setTransferStockItems([]);
        setTransferQuantities({});
        return;
      }
      setTransferLoading(true);
      setTransferError('');
      try {
        const details = await Promise.all(
          selectedTransferStockListIds.map((stockListId) =>
            getTransferStockListItemsByContext(branchId, stockListId, {
              mode: transferMode,
              sourceBranchId: effectiveTransferSourceBranchId,
            })
          )
        );
        const items = details.flatMap((detail) => {
          const listId = Number(detail?.stockList?.id);
          const listName = detail?.stockList?.name || `Lista #${detail?.stockList?.id}`;
          return (Array.isArray(detail?.items) ? detail.items : []).map((stock) => ({
            ...stock,
            sourceStockListId: listId,
            sourceStockListName: listName,
          }));
        });
        setTransferStockItems(items);
        setTransferQuantities(
          items.reduce((acc, stock) => {
            acc[stock.id] = '';
            return acc;
          }, {})
        );
      } catch (err) {
        setTransferError(err.response?.data?.message || 'No se pudo cargar la lista seleccionada.');
      } finally {
        setTransferLoading(false);
      }
    };
    fetchStockListDetail();
  }, [
    activeTab,
    transferMode,
    effectiveTransferSourceBranchId,
    selectedTransferStockListIds,
    transferRefreshKey,
    canReadStock,
    canTransferGuide,
    branchId,
  ]);

  useEffect(() => {
    const fetchPendingRequests = async () => {
      if (activeTab !== 'transfer') return;
      if (transferMode !== 'outbound') return;
      if (!transferDestinationBranchId) {
        setTransferPendingRequests([]);
        setTransferRequestOptions([]);
        return;
      }
      try {
        const response = await getIncomingPendingStockRequests(branchId, {
          pageSize: 0,
          requestingBranchId: transferDestinationBranchId,
        });
        const normalized = normalizePaginatedResponse(response);
        const pending = normalized.data || [];
        setTransferPendingRequests(pending);
        setTransferRequestOptions(
          pending.map((entry) => ({
            id: entry.id,
            label: `${entry.name || `Solicitud #${entry.id}`} (${Number(entry.progress?.remainingTotal || 0).toFixed(2)})`,
          }))
        );
      } catch (err) {
        setTransferPendingRequests([]);
        setTransferRequestOptions([]);
        setTransferError(err.response?.data?.message || 'No se pudieron cargar las solicitudes pendientes.');
      }
    };
    fetchPendingRequests();
  }, [activeTab, transferMode, transferDestinationBranchId, branchId]);

  useEffect(() => {
    if (activeTab !== 'transfer') return;
    if (transferMode !== 'outbound') return;
    setTransferRequestSelections([]);
    setTransferPrefillNotice('');
    setAutoPrefillContextKey('');
  }, [activeTab, transferMode, transferDestinationBranchId]);

  useEffect(() => {
    if (!pendingPrefillRequestListId) return;
    if (activeTab !== 'transfer' || transferMode !== 'outbound') return;
    if (!transferDestinationBranchId) return;
    if (!transferRequestOptions.length) return;

    const option = transferRequestOptions.find(
      (entry) => Number(entry.id) === Number(pendingPrefillRequestListId)
    );
    if (option) {
      setTransferRequestSelections([option]);
      setTransferPrefillNotice('Solicitud preseleccionada desde revisión.');
    } else {
      setTransferPrefillNotice('No se pudo preseleccionar la solicitud (no está pendiente para este destino).');
    }
    setPendingPrefillRequestListId(null);
  }, [
    pendingPrefillRequestListId,
    activeTab,
    transferMode,
    transferDestinationBranchId,
    transferRequestOptions,
  ]);

  const filteredGuides = useMemo(() => {
    if (!typeFilter) return guides;
    return guides.filter((guide) => guide.guideType === typeFilter);
  }, [guides, typeFilter]);

  const statusOptions = useMemo(() => {
    const map = new Map(
      DELIVERY_GUIDE_STATUS_OPTIONS.map((option) => [option.value, option])
    );
    guides.forEach((guide) => {
      const normalized = normalizeGuideStatus(guide.status);
      if (!normalized || map.has(normalized)) return;
      map.set(normalized, {
        value: normalized,
        label: getDeliveryGuideStatusLabel(normalized),
        tone: getDeliveryGuideStatusTone(normalized),
      });
    });
    return Array.from(map.values());
  }, [guides]);

  const toggleStatusFilter = (value) => {
    setStatusFilters((prev) => {
      if (prev.includes(value)) {
        return prev.filter((entry) => entry !== value);
      }
      return [...prev, value];
    });
  };

  const clearStatusFilters = () => {
    setStatusFilters([]);
  };

  const renderContext = (guide) => {
    if (guide.guideType === 'transfer') {
      const source = guide.sourceBranchId || '-';
      const destination = guide.destinationBranchId || '-';
      return `Origen ${source} / Destino ${destination}`;
    }
    return guide?.Purchase?.Client?.name || '-';
  };

  const renderReference = (guide) => {
    if (guide.guideType === 'transfer') {
      return 'Traslado entre sucursales';
    }
    return guide.purchaseId ? `Venta #${guide.purchaseId}` : 'Venta';
  };

  const filteredSales = useMemo(() => sales || [], [sales]);

  const toggleSaleSelection = (purchaseId) => {
    if (!canBulkCreate) return;
    setSelectedSales((prev) => {
      if (prev.includes(purchaseId)) {
        return prev.filter((id) => id !== purchaseId);
      }
      return [...prev, purchaseId];
    });
  };

  const clearSelection = () => {
    setSelectedSales([]);
  };

  const handleBulkPayloadChange = (field, value) => {
    if (!canBulkCreate) return;
    setBulkPayload((prev) => ({ ...prev, [field]: value }));
  };

  const handleBulkCreate = async () => {
    if (!canBulkCreate) return;
    if (!branchId) return;
    if (selectedSales.length === 0) {
      setBulkError('Selecciona al menos una venta para crear las guías.');
      setBulkSuccess('');
      return;
    }
    setBulkSubmitting(true);
    setBulkError('');
    setBulkSuccess('');
    try {
      const payload = {
        purchaseIds: selectedSales,
        driverName: bulkPayload.driverName || null,
        vehiclePlate: bulkPayload.vehiclePlate || null,
        deliveryDate: bulkPayload.deliveryDate || null,
        notes: bulkPayload.notes || null,
      };
      const result = await createBulkDeliveryGuides(branchId, payload);
      const createdCount = result?.created?.length || 0;
      const skippedCount = result?.skipped?.length || 0;
      const failedCount = result?.failed?.length || 0;
      const invalidCount = result?.invalidIds?.length || 0;
      setBulkSuccess(
        `Guías creadas: ${createdCount}. ` +
          `Saltadas: ${skippedCount}. ` +
          `Fallidas: ${failedCount}. ` +
          (invalidCount ? `Invalidas: ${invalidCount}.` : '')
      );
      clearSelection();
      setBulkPayload({ driverName: '', vehiclePlate: '', deliveryDate: '', notes: '' });
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setBulkError(err.response?.data?.message || 'No se pudo crear las guías.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  const handleRefresh = () => {
    if (activeTab === 'guides') {
      setRefreshKey((prev) => prev + 1);
      return;
    }
    if (activeTab === 'sales') {
      setSalesRefreshKey((prev) => prev + 1);
      return;
    } else {
      setTransferRefreshKey((prev) => prev + 1);
    }
  };

  const destinationBranch = branches.find((branch) => String(branch.id) === String(branchId));
  const sourceBranchOptions = transferBranchOptions;
  const destinationBranchOptions = transferBranchOptions;

  const handleTransferQuantityChange = (stockId, availableQuantity, value) => {
    if (!canTransferGuide) return;
    const normalized = value.replace(',', '.');
    const parsed = parseFloat(normalized);
    if (Number.isNaN(parsed) || parsed < 0) {
      setTransferQuantities((prev) => ({ ...prev, [stockId]: '' }));
      return;
    }
    if (parsed > availableQuantity) {
      setTransferQuantities((prev) => ({ ...prev, [stockId]: availableQuantity }));
      return;
    }
    setTransferQuantities((prev) => ({ ...prev, [stockId]: normalized }));
  };

  const handleTransferSetMax = (stockId, availableQuantity) => {
    if (!canTransferGuide) return;
    setTransferQuantities((prev) => ({ ...prev, [stockId]: availableQuantity }));
  };

  const handleTransferPayloadChange = (field, value) => {
    if (!canTransferGuide) return;
    setTransferPayload((prev) => ({ ...prev, [field]: value }));
  };

  const handleTransferExpenseChange = (index, field, value) => {
    if (!canTransferGuide) return;
    setTransferExpenses((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const handleAddTransferExpense = () => {
    if (!canTransferGuide) return;
    setTransferExpenses((prev) => [...prev, { title: '', description: '', amount: '' }]);
  };

  const handleRemoveTransferExpense = (index) => {
    if (!canTransferGuide) return;
    setTransferExpenses((prev) => prev.filter((_, i) => i !== index));
  };

  const applyPrefillFromRequests = useCallback((requestSelections, source = 'manual') => {
    if (transferMode !== 'outbound') return;
    if (!requestSelections.length) {
      setTransferPrefillNotice(
        source === 'auto'
          ? 'No hay solicitudes pendientes para prefijar.'
          : 'Selecciona al menos una solicitud para prefijar cantidades.'
      );
      return;
    }

    const selectedIds = new Set(requestSelections.map((entry) => Number(entry.id)));
    const selectedLists = transferPendingRequests.filter((entry) => selectedIds.has(Number(entry.id)));
    if (!selectedLists.length) {
      setTransferPrefillNotice(
        source === 'auto'
          ? 'No se encontraron solicitudes pendientes para la selección actual.'
          : 'No se encontraron solicitudes pendientes para la selección actual.'
      );
      return;
    }
    const varietyNeeded = new Map();
    const varietyLabels = new Map();

    selectedLists.forEach((list) => {
      (list.Items || []).forEach((item) => {
        const varietyId = Number(item.varietyId);
        if (!Number.isInteger(varietyId)) return;
        const remaining = Math.max(
          0,
          Number(item.requestedQuantity || 0) - Number(item.fulfilledQuantity || 0)
        );
        if (remaining <= 0) return;
        varietyNeeded.set(varietyId, (varietyNeeded.get(varietyId) || 0) + remaining);
        if (!varietyLabels.has(varietyId)) {
          varietyLabels.set(varietyId, getRequestItemDisplayName(item));
        }
      });
    });

    const nextQuantities = {};
    transferStockItems.forEach((stock) => {
      nextQuantities[stock.id] = '';
    });

    const missingVarieties = new Set();
    varietyNeeded.forEach((requiredQty, varietyId) => {
      let remaining = requiredQty;
      const matchingStocks = transferStockItems.filter(
        (stock) => Number(stock.varietyId) === Number(varietyId)
      );
      if (!matchingStocks.length) {
        missingVarieties.add(varietyId);
        return;
      }
      matchingStocks.forEach((stock) => {
        if (remaining <= 0) return;
        const available = Number(stock.quantity || 0);
        if (!Number.isFinite(available) || available <= 0) return;
        const assign = Math.min(remaining, available);
        if (assign <= 0) return;
        nextQuantities[stock.id] = Number(assign.toFixed(3));
        remaining -= assign;
      });
      if (remaining > 1e-6) {
        missingVarieties.add(varietyId);
      }
    });

    setTransferQuantities((prev) => ({
      ...prev,
      ...nextQuantities,
    }));

    if (missingVarieties.size) {
      const missingLabels = Array.from(missingVarieties).map(
        (varietyId) => varietyLabels.get(varietyId) || `Variedad #${varietyId}`
      );
      setTransferPrefillNotice(
        `${source === 'auto' ? 'Prefill automatico parcial' : 'Prefill parcial'}. Faltan cantidades para: ${missingLabels.join(', ')}.`
      );
    } else {
      setTransferPrefillNotice(
        source === 'auto'
          ? 'Cantidades precargadas automaticamente desde solicitudes pendientes.'
          : 'Cantidades precargadas desde solicitudes seleccionadas.'
      );
    }
  }, [transferMode, transferPendingRequests, transferStockItems]);

  const handlePrefillFromRequests = () => {
    applyPrefillFromRequests(transferRequestSelections, 'manual');
  };

  useEffect(() => {
    if (activeTab !== 'transfer') return;
    if (transferMode !== 'outbound') return;
    if (!selectedTransferStockListIds.length) return;
    if (!transferStockItems.length) return;
    if (!transferDestinationBranchId) return;
    if (pendingPrefillRequestListId) return;

    const selectedForPrefill = transferRequestSelections.length
      ? transferRequestSelections
      : transferRequestOptions;
    const selectionKey = selectedForPrefill
      .map((entry) => Number(entry.id))
      .filter((entry) => Number.isInteger(entry))
      .sort((a, b) => a - b)
      .join(',');

    const contextKey = [
      transferDestinationBranchId,
      selectedTransferStockListIds.join(','),
      transferStockItems.length,
      transferPendingRequests.map((entry) => entry.id).join(','),
      selectionKey || 'none',
    ].join('|');

    if (contextKey === autoPrefillContextKey) return;

    if (!transferRequestSelections.length && selectedForPrefill.length) {
      setTransferRequestSelections(selectedForPrefill);
    }

    applyPrefillFromRequests(selectedForPrefill, 'auto');
    setAutoPrefillContextKey(contextKey);
  }, [
    activeTab,
    transferMode,
    selectedTransferStockListIds,
    transferStockItems,
    transferDestinationBranchId,
    transferRequestSelections,
    transferRequestOptions,
    transferPendingRequests,
    pendingPrefillRequestListId,
    autoPrefillContextKey,
    applyPrefillFromRequests,
  ]);

  const groupedTransferStockItems = useMemo(() => {
    const groups = new Map();
    transferStockItems.forEach((stock) => {
      const listId = Number(stock.sourceStockListId);
      const key = Number.isInteger(listId) ? listId : -1;
      if (!groups.has(key)) {
        groups.set(key, {
          stockListId: key,
          stockListName: stock.sourceStockListName || `Lista #${key}`,
          items: [],
        });
      }
      groups.get(key).items.push(stock);
    });

    const orderMap = new Map();
    selectedTransferStockListIds.forEach((id, index) => {
      orderMap.set(id, index);
    });

    return Array.from(groups.values()).sort((a, b) => {
      const aOrder = orderMap.has(a.stockListId) ? orderMap.get(a.stockListId) : Number.MAX_SAFE_INTEGER;
      const bOrder = orderMap.has(b.stockListId) ? orderMap.get(b.stockListId) : Number.MAX_SAFE_INTEGER;
      if (aOrder !== bOrder) return aOrder - bOrder;
      return String(a.stockListName || '').localeCompare(String(b.stockListName || ''));
    });
  }, [transferStockItems, selectedTransferStockListIds]);

  const handleTransferSubmit = async () => {
    if (!canTransferGuide) return;
    if (!branchId) return;
    if (!effectiveTransferSourceBranchId) {
      setTransferError('Selecciona la sucursal origen.');
      return;
    }
    if (transferMode === 'outbound' && !transferDestinationBranchId) {
      setTransferError('Selecciona la sucursal destino.');
      return;
    }
    if (!selectedTransferStockListIds.length) {
      setTransferError('Selecciona al menos una lista de stock origen.');
      return;
    }

    const items = transferStockItems
      .map((stock) => {
        const value = transferQuantities[stock.id];
        if (value === '' || value === null || value === undefined) return null;
        const parsed = parseFloat(String(value).replace(',', '.'));
        if (!Number.isFinite(parsed) || parsed <= 0) return null;
        return { stockId: stock.id, quantity: parsed };
      })
      .filter(Boolean);

    if (!items.length) {
      setTransferError('Selecciona al menos un item para trasladar.');
      return;
    }

    const expensesPayload = transferExpenses
      .map((expense) => ({
        title: (expense.title || '').trim(),
        description: (expense.description || '').trim() || null,
        amount: Number(String(expense.amount || '').replace(',', '.')),
      }))
      .filter((expense) => expense.title || expense.description || expense.amount);

    for (const expense of expensesPayload) {
      if (!expense.title || !Number.isFinite(expense.amount) || expense.amount <= 0) {
        setTransferError('Completa titulo y monto valido para cada gasto adicional.');
        return;
      }
    }

    setTransferSubmitting(true);
    setTransferError('');
    setTransferSuccess('');
    try {
      const selectedRequestListIds = transferRequestSelections
        .map((entry) => Number(entry.id))
        .filter((entry) => Number.isInteger(entry));
      const payload = {
        sourceBranchId: Number(effectiveTransferSourceBranchId),
        stockListId: Number(selectedTransferStockListIds[0]),
        stockListIds: selectedTransferStockListIds,
        items,
        additionalExpenses: expensesPayload,
        stockListName: transferPayload.stockListName || undefined,
        guideNumber: transferPayload.guideNumber || undefined,
        driverName: transferPayload.driverName || undefined,
        vehiclePlate: transferPayload.vehiclePlate || undefined,
        deliveryDate: transferPayload.deliveryDate || undefined,
        notes: transferPayload.notes || undefined,
      };
      let result;
      if (transferMode === 'outbound') {
        payload.destinationBranchId = Number(transferDestinationBranchId);
        payload.requestListIds = selectedRequestListIds;
        result = await createOutboundTransferDeliveryGuide(branchId, payload);
      } else {
        result = await createTransferDeliveryGuide(branchId, payload);
      }
      const guideId = result?.deliveryGuide?.id;
      setTransferSuccess(
        transferMode === 'outbound'
          ? 'Guía saliente creada correctamente.'
          : 'Guía de traslado creada correctamente.'
      );
      setTransferQuantities({});
      setTransferExpenses([]);
      setTransferRequestSelections([]);
      setTransferPrefillNotice('');
      setTransferPayload({
        stockListName: '',
        guideNumber: '',
        driverName: '',
        vehiclePlate: '',
        deliveryDate: '',
        notes: '',
      });
      if (guideId) {
        navigate(`/branches/${branchId}/delivery-guides/${guideId}/details`);
      }
      setRefreshKey((prev) => prev + 1);
    } catch (err) {
      setTransferError(err.response?.data?.message || 'No se pudo crear la guía de traslado.');
    } finally {
      setTransferSubmitting(false);
    }
  };

  const statusSummary = statusFilters.length
    ? statusFilters.map((status) => getDeliveryGuideStatusLabel(status)).join(', ')
    : 'Todos';

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <div className="max-w-6xl mx-auto space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold">Guías de despacho</h1>
            <p className="text-sm text-gray-600">
              Visualiza ventas, traslados y el estado de cada entrega.
            </p>
          </div>
          <button
            onClick={handleRefresh}
            className="bg-gray-200 text-gray-700 px-4 py-2 rounded hover:bg-gray-300 text-sm"
          >
            Refrescar
          </button>
        </div>


        <div className="flex flex-wrap gap-2">
          {canReadGuides && (
            <button
              onClick={() => setActiveTab('guides')}
              className={`px-4 py-2 rounded-full text-sm border ${
                activeTab === 'guides'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-blue-200'
              }`}
            >
              Guías
            </button>
          )}
          {canReadSales && (canCreateGuide || canBulkCreate) && (
            <button
              onClick={() => setActiveTab('sales')}
              className={`px-4 py-2 rounded-full text-sm border ${
                activeTab === 'sales'
                  ? 'bg-emerald-600 text-white border-emerald-600'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-emerald-200'
              }`}
            >
              Crear guía desde venta
            </button>
          )}
          {canTransferGuide && canReadStock && (
            <button
              onClick={() => setActiveTab('transfer')}
              className={`px-4 py-2 rounded-full text-sm border ${
                activeTab === 'transfer'
                  ? 'bg-amber-500 text-white border-amber-500'
                  : 'bg-white text-gray-700 border-gray-200 hover:border-amber-200'
              }`}
            >
              Traslado entre sucursales
            </button>
          )}
        </div>

        {activeTab === 'guides' && (
          <>
            <div className="bg-white/90 rounded-xl shadow p-4 mb-4 border border-blue-100">
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-4">
                <DateRangePresetPicker
                  label="Rango de fechas"
                  presetLabel="Rango rapido"
                  startLabel="Fecha inicio"
                  endLabel="Fecha fin"
                  startDate={startDate}
                  endDate={endDate}
                  onStartDateChange={setStartDate}
                  onEndDateChange={setEndDate}
                  containerClassName="md:col-span-2"
                  labelClassName="block text-sm font-medium text-gray-700 mb-1"
                  inputLabelClassName="block text-xs text-gray-600 mb-1"
                  selectClassName="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  inputClassName="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                />
                <SearchMultiSelect
                  label="Ventas (IDs)"
                  placeholder="Buscar o agregar ID..."
                  value={guidePurchaseSelections}
                  onChange={setGuidePurchaseSelections}
                  onSearch={async (query) => {
                    const trimmed = query.trim();
                    if (!trimmed) return [];
                    const numeric = Number.parseInt(trimmed, 10);
                    if (!Number.isInteger(numeric)) return [];
                    const data = await getDeliveryGuides(branchId, {
                      purchaseId: numeric,
                      pageSize: 10,
                      page: 1,
                    });
                    const normalized = normalizePaginatedResponse(data);
                    const seen = new Set();
                    return (normalized.data || [])
                      .map((guide) => ({
                        id: guide.purchaseId,
                        label: `#${guide.purchaseId} · ${guide.Purchase?.Client?.name || 'Sin cliente'}`,
                      }))
                      .filter((option) => {
                        if (!option.id || seen.has(option.id)) return false;
                        seen.add(option.id);
                        return true;
                      });
                  }}
                  allowCreate
                  createLabel={(query) => `Agregar #${query}`}
                  onCreateOption={(query) => {
                    const numeric = Number.parseInt(query, 10);
                    if (!Number.isInteger(numeric)) return null;
                    return { id: numeric, label: `#${numeric}` };
                  }}
                />
                <SearchMultiSelect
                  label="Clientes"
                  placeholder="Buscar por nombre o RUT..."
                  value={guideClientSelections}
                  onChange={setGuideClientSelections}
                  onSearch={async (query) => {
                    const trimmed = query.trim();
                    if (!trimmed) return [];
                    const data = await getClients({ search: trimmed, pageSize: 10 });
                    const normalized = normalizePaginatedResponse(data);
                    return (normalized.data || []).map((client) => ({
                      id: client.id,
                      label: `${client.name || 'Sin nombre'}${client.rut ? ` · ${client.rut}` : ''}`,
                    }));
                  }}
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tipo
                  </label>
                  <select
                    value={typeFilter}
                    onChange={(e) => setTypeFilter(e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  >
                    <option value="">Todos</option>
                    <option value="sale">Venta</option>
                    <option value="transfer">Traslado</option>
                  </select>
                </div>
              </div>
              <div className="mt-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  {statusOptions.map((option) => {
                    const isActive = statusFilters.includes(option.value);
                    const baseTone = isActive
                      ? option.tone
                      : 'bg-white text-gray-600 border-gray-200';
                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => toggleStatusFilter(option.value)}
                        className={`px-3 py-1 rounded-full text-xs border transition ${baseTone}`}
                        aria-pressed={isActive}
                      >
                        {option.label}
                      </button>
                    );
                  })}
                  {statusFilters.length > 0 && (
                    <button
                      type="button"
                      onClick={clearStatusFilters}
                      className="px-3 py-1 rounded-full text-xs border border-gray-300 text-gray-600 hover:bg-gray-100"
                    >
                      Limpiar
                    </button>
                  )}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Puedes seleccionar varios estados para filtrar.
                </p>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 text-red-700 px-4 py-2 rounded border border-red-200 mb-4 text-sm">
                {error}
              </div>
            )}

            {loading ? (
              <p className="text-center text-gray-600">
                <InlineLoading label="Cargando guías" />
              </p>
            ) : filteredGuides.length === 0 ? (
              <p className="text-center text-gray-600">No hay guías para los filtros seleccionados.</p>
            ) : (
              <div className="bg-white rounded-xl shadow overflow-x-auto border border-blue-100">
                <table className="min-w-full text-sm">
                  <thead className="bg-sky-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-sky-700">Guía</th>
                      <th className="px-4 py-3 text-left font-medium text-sky-700">Tipo</th>
                      <th className="px-4 py-3 text-left font-medium text-sky-700">Referencia</th>
                      <th className="px-4 py-3 text-left font-medium text-sky-700">Cliente / Sucursal</th>
                      <th className="px-4 py-3 text-left font-medium text-sky-700">Estado</th>
                      <th className="px-4 py-3 text-left font-medium text-sky-700">Fecha</th>
                      <th className="px-4 py-3 text-right font-medium text-sky-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredGuides.map((guide) => {
                      const guideLabel = GUIDE_TYPE_LABELS[guide.guideType] || 'Venta';
                      const dateValue = guide.deliveryDate || guide.createdAt;
                      const statusLabel = getDeliveryGuideStatusLabel(guide.status);
                      const statusTone = getDeliveryGuideStatusTone(guide.status);
                      return (
                        <tr key={guide.id} className="hover:bg-sky-50/40">
                          <td className="px-4 py-3">
                            <div className="font-semibold text-gray-800">#{guide.id}</div>
                            {guide.guideNumber && (
                              <div className="text-xs text-gray-500">Folio: {guide.guideNumber}</div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-50 text-blue-700 border border-blue-100">
                              {guideLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3">{renderReference(guide)}</td>
                          <td className="px-4 py-3">{renderContext(guide)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs border ${statusTone}`}>
                              {statusLabel}
                            </span>
                          </td>
                          <td className="px-4 py-3">{formatDate(dateValue)}</td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() =>
                                navigate(`/branches/${branchId}/delivery-guides/${guide.id}/details`)
                              }
                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                              Ver detalle
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {guidesTotalPages > 1 && (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-4">
                <div>
                  Mostrando {guidesTotal === 0 ? 0 : (guidesPage - 1) * 12 + 1}-
                  {Math.min(guidesPage * 12, guidesTotal)} de {guidesTotal}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setGuidesPage((prev) => Math.max(prev - 1, 1))}
                    disabled={guidesPage === 1}
                    className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span>
                    {guidesPage} / {guidesTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setGuidesPage((prev) => Math.min(prev + 1, guidesTotalPages))}
                    disabled={guidesPage === guidesTotalPages}
                    className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'sales' && (
          <>
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <DateRangePresetPicker
                  label="Rango de fechas"
                  presetLabel="Rango rápido"
                  startLabel="Desde"
                  endLabel="Hasta"
                  startDate={salesStartDate}
                  endDate={salesEndDate}
                  onStartDateChange={setSalesStartDate}
                  onEndDateChange={setSalesEndDate}
                  containerClassName="md:col-span-2"
                  labelClassName="block text-sm font-medium mb-1"
                  inputLabelClassName="block text-xs text-gray-600 mb-1"
                  selectClassName="border border-gray-300 rounded px-3 py-2 text-sm w-full"
                  inputClassName="border border-gray-300 rounded px-3 py-2 text-sm w-full"
                />
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Buscar venta o cliente
                  </label>
                  <input
                    type="text"
                    value={salesSearch}
                    onChange={(e) => setSalesSearch(e.target.value)}
                    placeholder="ID, nombre o RUT"
                    disabled={!canReadSales}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                  />
                </div>
                <div>
                  <SearchMultiSelect
                    label="Ventas (IDs)"
                    placeholder="Buscar por ID..."
                    value={salesSelections}
                    onChange={setSalesSelections}
                    onSearch={async (query) => {
                      const trimmed = query.trim();
                      if (!trimmed) return [];
                      const data = await getSalesMissingDeliveryGuides(branchId, {
                        search: trimmed,
                        pageSize: 10,
                        page: 1,
                      });
                      const normalized = normalizePaginatedResponse(data);
                      return (normalized.data || []).map((purchase) => ({
                        id: purchase.id,
                        label: `#${purchase.id} · ${purchase.Client?.name || 'Sin cliente'}`,
                      }));
                    }}
                    allowCreate
                    createLabel={(query) => `Agregar #${query}`}
                    onCreateOption={(query) => {
                      const numeric = Number.parseInt(query, 10);
                      if (!Number.isInteger(numeric)) return null;
                      return { id: numeric, label: `#${numeric}` };
                    }}
                  />
                </div>
                <div>
                  <SearchMultiSelect
                    label="Clientes"
                    placeholder="Buscar por nombre o RUT..."
                    value={salesClientSelections}
                    onChange={setSalesClientSelections}
                    onSearch={async (query) => {
                      const trimmed = query.trim();
                      if (!trimmed) return [];
                      const data = await getClients({ search: trimmed, pageSize: 10 });
                      const normalized = normalizePaginatedResponse(data);
                      return (normalized.data || []).map((client) => ({
                        id: client.id,
                        label: `${client.name || 'Sin nombre'}${client.rut ? ` · ${client.rut}` : ''}`,
                      }));
                    }}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Se muestran ventas sin guía de despacho. Usa filtros sólo si lo necesitas.
              </p>
            </div>

            {canBulkCreate ? (
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <h2 className="text-sm font-semibold text-gray-700 mb-3">Creación masiva</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Nombre del chofer
                    </label>
                    <input
                      type="text"
                      value={bulkPayload.driverName}
                      onChange={(e) => handleBulkPayloadChange('driverName', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Patente del vehículo
                    </label>
                    <input
                      type="text"
                      value={bulkPayload.vehiclePlate}
                      onChange={(e) => handleBulkPayloadChange('vehiclePlate', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Fecha de entrega
                    </label>
                    <input
                      type="date"
                      value={bulkPayload.deliveryDate}
                      onChange={(e) => handleBulkPayloadChange('deliveryDate', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notas
                    </label>
                    <input
                      type="text"
                      value={bulkPayload.notes}
                      onChange={(e) => handleBulkPayloadChange('notes', e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    />
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 mt-4">
                  <button
                    onClick={handleBulkCreate}
                    disabled={bulkSubmitting}
                    className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                  >
                    {bulkSubmitting ? 'Creando...' : `Crear ${selectedSales.length || 0} guías`}
                  </button>
                  <button
                    onClick={clearSelection}
                    className="bg-gray-200 text-gray-700 px-4 py-2 rounded text-sm hover:bg-gray-300"
                  >
                    Limpiar seleccion
                  </button>
                </div>
                {bulkError && <p className="text-sm text-red-600 mt-2">{bulkError}</p>}
                {bulkSuccess && <p className="text-sm text-green-600 mt-2">{bulkSuccess}</p>}
              </div>
            ) : (
              <div className="bg-white rounded-lg shadow p-4 mb-4">
                <p className="text-sm text-gray-600">No tienes permisos para crear guías en bulk.</p>
              </div>
            )}

            {salesError && (
              <div className="bg-red-100 text-red-700 px-4 py-2 rounded mb-4 text-sm">
                {salesError}
              </div>
            )}

            {salesLoading ? (
              <p className="text-center text-gray-600">
                <InlineLoading label="Cargando ventas" />
              </p>
            ) : filteredSales.length === 0 ? (
              <p className="text-center text-gray-600">No hay ventas para los filtros seleccionados.</p>
            ) : (
              <div className="bg-white rounded-lg shadow overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Seleccion</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Venta</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Cliente</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Estado</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Total</th>
                      <th className="px-4 py-3 text-left font-medium text-gray-600">Fecha</th>
                      <th className="px-4 py-3 text-right font-medium text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {filteredSales.map((purchase) => (
                      <tr key={purchase.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3">
                          <input
                            type="checkbox"
                            checked={selectedSales.includes(purchase.id)}
                            onChange={() => toggleSaleSelection(purchase.id)}
                            disabled={!canBulkCreate}
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="font-semibold text-gray-800">#{purchase.id}</div>
                          {purchase.documentType && (
                            <div className="text-xs text-gray-500">
                              Documento: {purchase.documentType}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">
                            {purchase.Client?.name || 'Sin cliente'}
                          </div>
                          {purchase.Client?.rut && (
                            <div className="text-xs text-gray-500">{purchase.Client.rut}</div>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <div className="text-gray-900">
                            {(() => {
                              const statusMeta = getSaleStatusMeta(purchase.saleStatus);
                              return (
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold ${statusMeta.badgeClassName}`}>
                                  {statusMeta.label}
                                </span>
                              );
                            })()}
                          </div>
                          {purchase.paymentStatus && (
                            <div className="text-xs text-gray-500">
                              Pago: {purchase.paymentStatus}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3">{formatCurrency(purchase.totalAmount)}</td>
                        <td className="px-4 py-3">{formatDate(purchase.createdAt)}</td>
                        <td className="px-4 py-3 text-right">
                          {canCreateGuide && (
                            <button
                              onClick={() =>
                                navigate(
                                  `/branches/${branchId}/purchases/${purchase.id}/delivery-guides/new`
                                )
                              }
                              className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                            >
                              Crear guía
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {salesTotalPages > 1 && (
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-sm text-gray-500 mt-4">
                <div>
                  Mostrando {salesTotal === 0 ? 0 : (salesPage - 1) * 12 + 1}-
                  {Math.min(salesPage * 12, salesTotal)} de {salesTotal}
                </div>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setSalesPage((prev) => Math.max(prev - 1, 1))}
                    disabled={salesPage === 1}
                    className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                  >
                    Anterior
                  </button>
                  <span>
                    {salesPage} / {salesTotalPages}
                  </span>
                  <button
                    type="button"
                    onClick={() => setSalesPage((prev) => Math.min(prev + 1, salesTotalPages))}
                    disabled={salesPage === salesTotalPages}
                    className="px-3 py-1 rounded border text-gray-600 disabled:opacity-50"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {activeTab === 'transfer' && (
          <>
            <div className="bg-white rounded-lg shadow p-4 mb-4">
              {isMainTransferBranch && (
                <div className="mb-4 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => setTransferMode('outbound')}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      transferMode === 'outbound'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    Enviar desde principal
                  </button>
                  <button
                    type="button"
                    onClick={() => setTransferMode('inbound')}
                    className={`px-3 py-1 rounded-full text-sm border ${
                      transferMode === 'inbound'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    Recibir desde sucursal
                  </button>
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {transferMode === 'outbound' ? 'Sucursal origen' : 'Sucursal destino'}
                  </label>
                  <input
                    type="text"
                    value={destinationBranch?.branchName || `Sucursal ${branchId}`}
                    disabled
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {transferMode === 'outbound' ? 'Sucursal destino' : 'Sucursal origen'}
                  </label>
                  {transferMode === 'outbound' ? (
                    <select
                      value={transferDestinationBranchId}
                      onChange={(e) => setTransferDestinationBranchId(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      disabled={!canTransferGuide}
                    >
                      <option value="">Selecciona sucursal destino</option>
                      {destinationBranchOptions.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.branchName}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <select
                      value={transferSourceBranchId}
                      onChange={(e) => setTransferSourceBranchId(e.target.value)}
                      className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                      disabled={!canTransferGuide}
                    >
                      <option value="">Selecciona sucursal origen</option>
                      {sourceBranchOptions.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.branchName}
                        </option>
                      ))}
                    </select>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Listas de stock origen
                  </label>
                  <SearchMultiSelect
                    placeholder="Selecciona una o más listas..."
                    value={transferStockListSelections}
                    onChange={setTransferStockListSelections}
                    options={(transferStockLists || []).map((list) => ({
                      id: list.id,
                      label: list.name || `Lista #${list.id}`,
                    }))}
                    onSearch={async (query) => {
                      const normalized = String(query || '').trim().toLowerCase();
                      const options = (transferStockLists || []).map((list) => ({
                        id: list.id,
                        label: list.name || `Lista #${list.id}`,
                      }));
                      if (!normalized) return options;
                      return options.filter((entry) =>
                        String(entry.label || '').toLowerCase().includes(normalized)
                      );
                    }}
                    disabled={!canTransferGuide || !effectiveTransferSourceBranchId}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Nombre lista destino (opcional)
                  </label>
                  <input
                    type="text"
                    value={transferPayload.stockListName}
                    onChange={(e) => handleTransferPayloadChange('stockListName', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    disabled={!canTransferGuide}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Folio guía (opcional)
                  </label>
                  <input
                    type="text"
                    value={transferPayload.guideNumber}
                    onChange={(e) => handleTransferPayloadChange('guideNumber', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    disabled={!canTransferGuide}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fecha de entrega
                  </label>
                  <input
                    type="date"
                    value={transferPayload.deliveryDate}
                    onChange={(e) => handleTransferPayloadChange('deliveryDate', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    disabled={!canTransferGuide}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Chofer
                  </label>
                  <input
                    type="text"
                    value={transferPayload.driverName}
                    onChange={(e) => handleTransferPayloadChange('driverName', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    disabled={!canTransferGuide}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Patente
                  </label>
                  <input
                    type="text"
                    value={transferPayload.vehiclePlate}
                    onChange={(e) => handleTransferPayloadChange('vehiclePlate', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    disabled={!canTransferGuide}
                  />
                </div>
                {transferMode === 'outbound' && (
                  <div className="md:col-span-2 space-y-2">
                    <SearchMultiSelect
                      label="Solicitudes pendientes"
                      placeholder="Buscar solicitud..."
                      value={transferRequestSelections}
                      onChange={setTransferRequestSelections}
                      options={transferRequestOptions}
                      onSearch={async (query) => {
                        const normalized = String(query || '').trim().toLowerCase();
                        const options = transferRequestOptions || [];
                        if (!normalized) return options;
                        return options.filter((entry) =>
                          String(entry.label || '').toLowerCase().includes(normalized)
                        );
                      }}
                      disabled={!canTransferGuide || !transferDestinationBranchId}
                    />
                    <button
                      type="button"
                      onClick={handlePrefillFromRequests}
                      disabled={!canTransferGuide || !transferRequestSelections.length}
                      className="text-sm text-blue-600 hover:text-blue-700 disabled:opacity-60"
                    >
                      Agregar desde solicitudes seleccionadas
                    </button>
                    {transferPrefillNotice && (
                      <p className="text-xs text-gray-600">{transferPrefillNotice}</p>
                    )}
                  </div>
                )}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notas
                  </label>
                  <textarea
                    rows={3}
                    value={transferPayload.notes}
                    onChange={(e) => handleTransferPayloadChange('notes', e.target.value)}
                    className="w-full border border-gray-300 rounded px-3 py-2 text-sm"
                    disabled={!canTransferGuide}
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-3">
                Los costos se ponderan por peso (kg) y los gastos adicionales se prorratean.
              </p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Items a trasladar</h2>
              </div>
              {transferLoading ? (
                <p className="text-sm text-gray-600">
                  <InlineLoading label="Cargando items" />
                </p>
              ) : transferStockItems.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Selecciona una o más listas para ver los productos disponibles.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Producto</th>
                        <th className="px-3 py-2 text-left font-medium text-gray-600">Unidad</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Disponible</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Precio</th>
                        <th className="px-3 py-2 text-right font-medium text-gray-600">Cantidad</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {groupedTransferStockItems.map((group) => (
                        <React.Fragment key={`group-${group.stockListId}`}>
                          <tr className="bg-slate-50">
                            <td className="px-3 py-2 font-semibold text-slate-700" colSpan={5}>
                              {group.stockListName}
                            </td>
                          </tr>
                          {group.items.map((stock) => {
                            const available = Number(stock.quantity || 0);
                            return (
                              <tr key={stock.id}>
                                <td className="px-3 py-2">{stock.varietyName}</td>
                                <td className="px-3 py-2">{stock.metricUnit || 'unidad'}</td>
                                <td className="px-3 py-2 text-right">{available}</td>
                                <td className="px-3 py-2 text-right">{formatCurrency(stock.price)}</td>
                                <td className="px-3 py-2 text-right">
                                  <div className="flex items-center justify-end gap-2">
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      value={transferQuantities[stock.id] ?? ''}
                                      onChange={(e) =>
                                        handleTransferQuantityChange(stock.id, available, e.target.value)
                                      }
                                      className="w-24 border border-gray-300 rounded px-2 py-1 text-sm text-right"
                                      disabled={!canTransferGuide}
                                    />
                                    <button
                                      type="button"
                                      onClick={() => handleTransferSetMax(stock.id, available)}
                                      className="text-xs text-blue-600 hover:text-blue-700"
                                      disabled={!canTransferGuide}
                                    >
                                      Max
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow p-4 mb-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-sm font-semibold text-gray-700">Gastos adicionales</h2>
                {canTransferGuide && (
                  <button
                    type="button"
                    onClick={handleAddTransferExpense}
                    className="text-sm text-blue-600 hover:text-blue-700"
                  >
                    + Agregar gasto
                  </button>
                )}
              </div>
              {transferExpenses.length === 0 ? (
                <p className="text-sm text-gray-600">
                  Puedes agregar gastos como flete o personal adicional.
                </p>
              ) : (
                <div className="space-y-2">
                  {transferExpenses.map((expense, index) => (
                    <div key={index} className="grid grid-cols-1 md:grid-cols-4 gap-2">
                      <input
                        type="text"
                        value={expense.title}
                        onChange={(e) => handleTransferExpenseChange(index, 'title', e.target.value)}
                        placeholder="Titulo"
                        className="border border-gray-300 rounded px-3 py-2 text-sm"
                        disabled={!canTransferGuide}
                      />
                      <input
                        type="text"
                        value={expense.description}
                        onChange={(e) =>
                          handleTransferExpenseChange(index, 'description', e.target.value)
                        }
                        placeholder="Descripcion (opcional)"
                        className="border border-gray-300 rounded px-3 py-2 text-sm"
                        disabled={!canTransferGuide}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={expense.amount}
                        onChange={(e) =>
                          handleTransferExpenseChange(index, 'amount', sanitizeNumericInput(e.target.value))
                        }
                        placeholder="Monto"
                        className="border border-gray-300 rounded px-3 py-2 text-sm"
                        disabled={!canTransferGuide}
                      />
                      {canTransferGuide && (
                        <button
                          type="button"
                          onClick={() => handleRemoveTransferExpense(index)}
                          className="text-sm text-red-600 hover:text-red-700"
                        >
                          Quitar
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              {canTransferGuide && (
                <button
                  type="button"
                  onClick={handleTransferSubmit}
                  disabled={transferSubmitting}
                  className="bg-blue-600 text-white px-4 py-2 rounded text-sm hover:bg-blue-700 disabled:opacity-60"
                >
                  {transferSubmitting
                    ? 'Creando...'
                    : transferMode === 'outbound'
                      ? 'Crear guía saliente'
                      : 'Crear guía de traslado'}
                </button>
              )}
              {transferError && <p className="text-sm text-red-600">{transferError}</p>}
              {transferSuccess && <p className="text-sm text-green-600">{transferSuccess}</p>}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default DeliveryGuidesListPage;
